import React from 'react'
import { router, usePage } from '@inertiajs/react'
import {
  Box, Button, Paper, Typography,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material'
import AppShell from '../../../../components/layout/AppShell'
import PageHeader from '../../../../components/ui/PageHeader'
import StatusBadge from '../../../../components/ui/StatusBadge'
import FormattedCurrency from '../../../../components/ui/FormattedCurrency'

Show.layout = (page) => <AppShell>{page}</AppShell>

export default function Show({ property, special_levy, unit_amounts = [] }) {
  const { props } = usePage()
  const tenant = props.tenant || {}

  const handleApprove = () => {
    router.patch(`/m/${tenant.slug}/properties/${property.id}/special_levies/${special_levy.id}/approve`)
  }

  const isDraft = special_levy.status === 'draft'
  const totalAmount = parseFloat(special_levy.amount || 0)

  return (
    <Box>
      <PageHeader
        title={special_levy.name}
        breadcrumbs={[
          { label: 'Sonderumlagen', href: `/m/${tenant.slug}/properties/${property.id}/special_levies` },
          { label: special_levy.name },
        ]}
        actions={
          isDraft && (
            <Button variant="contained" onClick={handleApprove} data-testid="approve-btn">
              Freigeben
            </Button>
          )
        }
      />

      {/* Summary card */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">Gesamtbetrag</Typography>
            <Typography variant="body2" fontWeight={500}><FormattedCurrency value={totalAmount} align="left" /></Typography>
          </Box>
          {[
            ['Beschluss', special_levy.resolution_number ? `#${special_levy.resolution_number} (${special_levy.resolution_date})` : '–'],
            ['Verteilerschlüssel', special_levy.distribution_key_name || '–'],
            ['Fälligkeitsdatum', special_levy.due_date || '–'],
            ['Raten', special_levy.installments],
          ].map(([label, value]) => (
            <Box key={label}>
              <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
              <Typography variant="body2" fontWeight={500}>{value}</Typography>
            </Box>
          ))}
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">Status</Typography>
            <StatusBadge type="special_levy_status" value={special_levy.status} />
          </Box>
        </Box>
      </Paper>

      {/* Per-unit breakdown */}
      <Typography variant="h6" fontWeight={600} gutterBottom>Verteilung nach Einheit</Typography>
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'background.subtle' }}>
              <TableCell sx={{ fontWeight: 600 }}>Einheit</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Eigentümer</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Gesamt</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Rate</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {unit_amounts.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                  <Typography color="text.secondary">Keine Verteilung berechnet.</Typography>
                </TableCell>
              </TableRow>
            )}
            {unit_amounts.map((ua) => (
              <TableRow key={ua.unit_id}>
                <TableCell><Typography variant="body2" fontWeight={500}>{ua.unit_number}</Typography></TableCell>
                <TableCell>{ua.owner_name || '–'}</TableCell>
                <TableCell align="right">{ua.total_amount ? <FormattedCurrency value={ua.total_amount} /> : '–'}</TableCell>
                <TableCell align="right">{ua.installment_amount ? <FormattedCurrency value={ua.installment_amount} /> : '–'}</TableCell>
              </TableRow>
            ))}
            {unit_amounts.length > 0 && (
              <TableRow sx={{ backgroundColor: 'background.subtle', fontWeight: 600 }}>
                <TableCell colSpan={2}><Typography variant="body2" fontWeight={600}>Gesamt</Typography></TableCell>
                <TableCell align="right"><Typography variant="body2" fontWeight={600}><FormattedCurrency value={totalAmount} /></Typography></TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={600}>
                    <FormattedCurrency value={totalAmount / special_levy.installments} />
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
